import React, { useState, useEffect, useMemo } from 'react';
import { Gem, Eye, User, DollarSign, Search, FileText } from 'lucide-react';
import PdfService from '../../services/PdfService';
import LoadEstimateModal from '../shared/LoadEstimateModal';
import PaymentConfirmModal from './PaymentConfirmModal';
import { applyExchangeRate } from '../../utils/calculations';
import {
  calculatePurityGoldRate,
  calculateSaleNetWeight,
  isKundanItemCode,
  isKundanCategory,
  calculateFineWeight,
  calculateGoldValue,
  calculateWastageAmount,
  calculateMakingAmount,
  calculateTotalGoldAmount,
  calculateCertificationCharges,
  calculateJewelValue,
  calculateUSDGrandTotal,
  stoneWeightToGrams,
  buildStoneCategoryTotals,
  calculateAcquisitionCost,
  calculateAcquisitionCostWithTax,
  calculateKundanFineWeight,
  calculateCostPanelResult,
  isSmallItemCode,
  calculateSmallItemPrice,
  applyKundanGoldCostDeduction,
} from '../../shared/utils/jewelryCalculations';
import PageIdentifier from '../shared/PageIdentifier';
import CostPanel, { RatePill } from '../shared/CostPanel';
import SCREEN_IDS from '../../utils/screenIds';
import CustomerLookup from '../shared/CustomerLookup';
import WhatsAppLinkService from '../../services/WhatsAppLinkService';
import EmailSendService from '../../services/EmailSendService';
import HistoricalRateService from '../../services/HistoricalRateService';
import EstimateDetailModal from '../estimates/EstimateDetailModal';
import usePermissions from '../../hooks/usePermissions';
import { todayCentral } from '../../shared/utils/dateUtils';

// Blur (remove focus) from any input when Enter is pressed
const blurOnEnter = (e) => { if (e.key === 'Enter') e.target.blur(); };

const USBilling = () => {
  // Ref for Item Code input
  const itemCodeRef = React.useRef(null);

  // Auto-focus item code field on page load
  useEffect(() => { itemCodeRef.current?.focus(); }, []);

  // Session-based authentication
  const { hasPermission } = usePermissions();
  const canEditRate = hasPermission('us-billing', 'edit');

  // Categories for wastage/making lookup
  const [categories, setCategories] = useState([]);

  // Section 1: Header & Rates
  const [date, setDate] = useState(() => {
    const today = new Date();
    // Get local date in YYYY-MM-DD format
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [goldRate, setGoldRate] = useState('');
  const [goldRateEditable, setGoldRateEditable] = useState(false);
  const [dollarRate, setDollarRate] = useState('');
  // Today's live rates, captured once at mount — used to detect when goldRate/dollarRate
  // above have been overridden by a backdated Bill Date (historical rate lookup).
  const [todaysGoldRate, setTodaysGoldRate] = useState(null);
  const [todaysDollarRate, setTodaysDollarRate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Section 2: Jewelry & Gold Calculation
  const [itemCode, setItemCode] = useState('');
  const [jewelryName, setJewelryName] = useState('');
  const [purity, setPurity] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [fineWeight, setFineWeight] = useState('');
  const [finePercent, setFinePercent] = useState(null); // Fine % from jewelry_pieces (Kundan only)
  const [kundanWeight, setKundanWeight] = useState('');
  const [kundanPercent, setKundanPercent] = useState('');
  const [goldPrice, setGoldPrice] = useState('');
  const [goldValue, setGoldValue] = useState('');
  const [wastagePercent, setWastagePercent] = useState('');
  const [wastageAmount, setWastageAmount] = useState('');
  const [makingCharge, setMakingCharge] = useState('');
  const [makingAmount, setMakingAmount] = useState('');
  const [totalGoldAmount, setTotalGoldAmount] = useState('');

  // Jewelry image (for PDF watermark)
  const [jewelryImage, setJewelryImage] = useState(null);

  // Item code lookup status: null | 'found' | 'sold' | 'not_found'
  const [itemCodeStatus, setItemCodeStatus] = useState(null);
  const [itemCodeStatusMsg, setItemCodeStatusMsg] = useState('');

  // Stone calculations
  const [stoneTotal, setStoneTotal] = useState(0);
  const [stoneWeightTotal, setStoneWeightTotal] = useState(0);
  const [stonePurchaseCost, setStonePurchaseCost] = useState(0);
  const [fetchedStones, setFetchedStones] = useState([]);
  const [certificateDocuments, setCertificateDocuments] = useState([]);
  const [diamondCarats, setDiamondCarats] = useState(0);
  const [certificationRequired, setCertificationRequired] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);

  // Locked entry rates captured when item is scanned (from jewelry_pieces DB columns)
  const [entryRates, setEntryRates] = useState({ goldRate: null, dollarRate: null, shipmentCostUSD: 0, laborCharges: 0, otherCosts: 0, inventoryNetWeight: '' });

  // Grand Total (lifted from TotalSection for WhatsApp sharing)
  const [grandTotalUSD, setGrandTotalUSD] = useState('0.00');
  // Totals lifted for PDF generation
  const [pdfTotals, setPdfTotals] = useState({ totalTaxPercent: 18, taxTotalINR: '0.00', grandTotalINR: '0.00', certCharges: 0 });

  // Linked customer id (set when a customer is selected from lookup)
  const [customerId, setCustomerId] = useState(null);

  // Payment rows (collected from PaymentSection, posted after bill is saved)
  const [payments, setPayments] = useState([]);

  // Payment confirm modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Estimate pre-fill — set when converting from ViewEstimates
  const [estimateNumber, setEstimateNumber] = useState('');
  const [estimateId, setEstimateId] = useState(null);
  const [estimateBannerVisible, setEstimateBannerVisible] = useState(false);
  const [estimateCustomsPct, setEstimateCustomsPct] = useState(null); // null = use TotalSection default
  const [estimateDiscount, setEstimateDiscount] = useState(0);
  const [showLoadModal, setShowLoadModal] = useState(false);
  // Bumped on Clear to force TotalSection to remount, resetting its local
  // state (discount, Customs %) that has no other reset path.
  const [formResetKey, setFormResetKey] = useState(0);

  // Fetch rates and categories on mount
  const [goldRateError, setGoldRateError] = useState('');
  const [goldRateWarn, setGoldRateWarn] = useState('');

  useEffect(() => {
    const STALE_DAYS = 2;
    const daysSince = (dateStr) => dateStr
      ? Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
      : null;

    // Fetch gold rate
    fetch('/api/rates/gold/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate && data.rate.gold_24k != null && data.rate.gold_24k !== '') {
          setGoldRate(parseFloat(data.rate.gold_24k));
          setTodaysGoldRate(parseFloat(data.rate.gold_24k));
          setGoldRateEditable(false);
          const age = daysSince(data.rate.rate_date);
          if (!data.is_today && age !== null && age > STALE_DAYS) {
            setGoldRateError('');
            setGoldRateWarn('');
            setGoldRateEditable(true);
          } else if (!data.is_today) {
            setGoldRateWarn(`ℹ️ ${data.message}. Rate is auto-filled — you can edit if needed.`);
            setGoldRateError('');
            setGoldRateEditable(true);
          } else {
            setGoldRateError('');
            setGoldRateWarn('');
          }
        } else {
          setGoldRate('');
          setGoldRateError('Gold rate not found. Please enter today\'s rate in Manual Rate Entry (UTIL-003) or enter manually below.');
          setGoldRateWarn('');
          setGoldRateEditable(true);
        }
      })
      .catch(() => {
        // Network/server error reaching our own API — keep showing whatever rate was
        // already loaded (last known good rate) instead of blanking the field.
        setGoldRateError('Failed to fetch latest gold rate. Showing last known rate — please verify or enter manually.');
        setGoldRateWarn('');
        setGoldRateEditable(true);
      });

    // Fetch dollar rate
    fetch('/api/rates/dollar/today', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rate) {
          const rawRate = parseFloat(data.rate.usd_to_inr);
          const adjusted = applyExchangeRate(rawRate);
          setDollarRate(adjusted);
          setTodaysDollarRate(adjusted);
        }
      })
      .catch(() => {
        // Keep default value on error
      });

    // Fetch categories
    fetch('/api/categories', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          console.log('Categories loaded:', data); // Debug log
          setCategories(data);
        } else {
          setCategories([
            { id: 1, name: 'Rings', code: 'RNG', wastage_charges: 8, making_charges: 500 },
            { id: 2, name: 'Necklaces', code: 'DNS', wastage_charges: 10, making_charges: 800 },
            { id: 3, name: 'Earrings', code: 'EAR', wastage_charges: 6, making_charges: 400 }
          ]);
        }
      })
      .catch(() => {
        setCategories([
          { id: 1, name: 'Rings', code: 'RNG', wastage_charges: 8, making_charges: 500 },
          { id: 2, name: 'Necklaces', code: 'DNS', wastage_charges: 10, making_charges: 800 },
          { id: 3, name: 'Earrings', code: 'EAR', wastage_charges: 6, making_charges: 400 }
        ]);
      });
  }, []);

  // ── Estimate pre-fill on mount ────────────────────────────
  // When navigating here from ViewEstimates via "Convert to Sale",
  // window.__estimatePreFill holds the estimate data + any rate overrides.
  useEffect(() => {
    const prefill = window.__estimatePreFill;
    if (!prefill || prefill.currency !== 'USD') return;

    // Consume the payload so re-mounts don't re-apply it
    window.__estimatePreFill = null;

    // Customer
    setName(prefill.customerName || '');
    setPhone(prefill.customerPhone || '');
    setEmail(prefill.customerEmail || '');

    // Rates (may be overridden)
    if (prefill.goldRate) {
      setGoldRate(parseFloat(prefill.goldRate));
      setGoldRateEditable(true);
    }

    // Item code — then trigger auto-fetch. Stone rates/wastage/making are
    // passed as overrides applied AFTER the fetch completes (not set
    // separately beforehand) — fetchJewelryDetails's own stone-fetch and
    // category defaults would otherwise silently overwrite them a moment later.
    if (prefill.itemCode) {
      setItemCode(prefill.itemCode);
      const stoneOverrides = Array.isArray(prefill.stoneDetails) && prefill.stoneDetails.length > 0
        ? prefill.stoneDetails
        : null;
      setTimeout(() => fetchJewelryDetails(prefill.itemCode, {
        stones: stoneOverrides,
        wastage: prefill.wastage,
        making: prefill.making,
      }), 300);
    } else {
      if (prefill.wastage) setWastagePercent(String(prefill.wastage));
      if (prefill.making) setMakingCharge(String(prefill.making));
      if (Array.isArray(prefill.stoneDetails) && prefill.stoneDetails.length > 0) {
        setFetchedStones(prefill.stoneDetails);
      }
    }

    setEstimateNumber(prefill.estimateNumber || '');
    setEstimateId(prefill.estimateId || null);
    setEstimateBannerVisible(true);
    if (prefill.customsPct) {
      setEstimateCustomsPct(parseFloat(prefill.customsPct));
    }
    setEstimateDiscount(parseFloat(prefill.discount) || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-calculate net weight — includes the same purity-converted Kundan
  // weight adjustment as USDEstimate.js for Kundan (K-prefixed) items.
  useEffect(() => {
    const nw = calculateSaleNetWeight({ grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity });
    setNetWeight(parseFloat(grossWeight) > 0 ? nw.toFixed(3) : '');
  }, [grossWeight, stoneWeightTotal, itemCode, kundanWeight, purity]);

  // Fine weight
  useEffect(() => {
    if (netWeight && purity) {
      setFineWeight(calculateFineWeight(netWeight, purity).toFixed(3));
    } else {
      setFineWeight('');
    }
  }, [netWeight, purity]);

  // Purity-adjusted gold price
  useEffect(() => {
    if (goldRate && purity) {
      setGoldPrice(calculatePurityGoldRate(goldRate, purity).toFixed(2));
    } else {
      setGoldPrice('');
    }
  }, [goldRate, purity]);

  // Gold value
  useEffect(() => {
    if (goldPrice && netWeight) {
      setGoldValue(calculateGoldValue(goldPrice, netWeight).toFixed(2));
    } else {
      setGoldValue('');
    }
  }, [goldPrice, netWeight]);

  // Wastage amount (always rounds up — consistent with all other screens)
  useEffect(() => {
    if (netWeight && wastagePercent && goldPrice) {
      setWastageAmount(calculateWastageAmount(netWeight, wastagePercent, goldPrice).toFixed(3));
    } else {
      setWastageAmount('');
    }
  }, [netWeight, wastagePercent, goldPrice]);

  // Making amount
  useEffect(() => {
    if (netWeight && makingCharge) {
      setMakingAmount(calculateMakingAmount(netWeight, makingCharge).toFixed(2));
    } else {
      setMakingAmount('');
    }
  }, [netWeight, makingCharge]);

  // Total gold amount
  useEffect(() => {
    setTotalGoldAmount(calculateTotalGoldAmount(goldValue, wastageAmount, makingAmount).toFixed(2));
  }, [goldValue, wastageAmount, makingAmount]);

  // Date validation
  const handleDateChange = async (e) => {
    const val = e.target.value;
    const today = todayCentral();
    if (val > today) return; // Block future dates
    setDate(val);
    if (!val) return;

    try {
      const { gold, dollar } = await HistoricalRateService.getRates(val);
      const missing = [];
      if (gold?.gold_24k != null && gold.gold_24k !== '') {
        setGoldRate(parseFloat(gold.gold_24k));
        setGoldRateError('');
      } else {
        missing.push('gold');
      }
      if (dollar?.usd_to_inr != null && dollar.usd_to_inr !== '') {
        setDollarRate(applyExchangeRate(parseFloat(dollar.usd_to_inr)));
      } else {
        missing.push('dollar');
      }
      setGoldRateWarn(missing.length
        ? `No ${missing.join(' or ')} rate saved for ${val}. Existing values were kept.`
        : `Gold and dollar rates updated for ${val}.`);
      setGoldRateEditable(true);
    } catch (error) {
      setGoldRateError(error.message || 'Failed to fetch rates for selected date.');
      setGoldRateWarn('');
      setGoldRateEditable(true);
    }
  };

  // Fetch jewelry details by Item Code
  // estimateOverrides — if provided, applied AFTER all system defaults so they always win
  const fetchJewelryDetails = async (code, estimateOverrides = null) => {
    if (!code) return;
    let itemFound = false;
    try {
      const normalizedCode = code.trim().toUpperCase();

      // Extract category code from item code (e.g., DNS-1 -> DNS)
      const categoryCode = normalizedCode.split('-')[0];

      const res = await fetch(`/api/jewelry/details/${normalizedCode}`, { credentials: 'include' });
      const data = await res.json();
      if (data && data.code === normalizedCode) {
        // Block billing for any item that isn't In Stock — Sold, Reserved
        // (mid-checkout on another bill), Under Repair, etc. The 409 payload
        // only carries {error, status, code, message}, not the full item, so
        // this must be checked before assuming itemFound and reading fields
        // (gross_weight, stones, ...) that don't exist on this response.
        if (res.status === 409 || data.error === 'item_status') {
          setItemCodeStatus('sold');
          setItemCodeStatusMsg(data.message || `Item ${data.status || 'unavailable'}`);
          // Clear any auto-populated fields
          setJewelryName('');
          setPurity('');
          setGrossWeight('');
          setNetWeight('');
          setFinePercent(null);
          setFetchedStones([]);
          setCertificateDocuments([]);
          setDiamondCarats(0);
          setCertificationRequired(false);
          setCategoryInfo(null);
          setWastagePercent('');
          setMakingCharge('');
          setEntryRates({ goldRate: null, dollarRate: null, shipmentCostUSD: 0, laborCharges: 0, otherCosts: 0, inventoryNetWeight: '' });
          itemCodeRef.current?.focus();
          return;
        }

        itemFound = true;
        setItemCodeStatus(null);
        setItemCodeStatusMsg('');
        setJewelryName(data.description?.trim() || data.code || ''); // description, else item code
        setPurity(data.gold_purity || '');
        setGrossWeight(data.gross_weight || '');
        setNetWeight(data.net_weight || '');
        setFinePercent(data.fine_percentage ? parseFloat(data.fine_percentage) : null);
        setKundanWeight(data.kundan_weight ? String(parseFloat(data.kundan_weight)) : '');
        setKundanPercent(data.kundan_percentage ? String(parseFloat(data.kundan_percentage)) : '');
        setJewelryImage(data.image_url || null);
        setCertificateDocuments(data.certificates || []);
        setEntryRates({
          goldRate: data.gold_rate_at_entry ? parseFloat(data.gold_rate_at_entry) : null,
          dollarRate: data.dollar_rate_at_entry ? parseFloat(data.dollar_rate_at_entry) : null,
          shipmentCostUSD: data.shipment_cost_usd ? parseFloat(data.shipment_cost_usd) : 0,
          laborCharges: data.labor_charges ? parseFloat(data.labor_charges) : 0,
          otherCosts: data.other_costs ? parseFloat(data.other_costs) : 0,
          inventoryNetWeight: data.net_weight || ''
        });

        // Set certification requirement from jewelry_pieces table
        setCertificationRequired(data.certificate === 'Yes');

        // Auto-populate stones
        const stones = data.stones || [];
        setFetchedStones(stones);

        // Calculate total diamond carats if certification is required
        if (data.certificate === 'Yes') {
          await calculateDiamondCarats(data.id, stones);
        } else {
          setDiamondCarats(0);
        }
      } else {
        setItemCodeStatus('not_found');
        setItemCodeStatusMsg('Item Not Found');
        setFetchedStones([]);
        setCertificateDocuments([]);
        setDiamondCarats(0);
        setCertificationRequired(false);
        itemCodeRef.current?.focus();
      }

      // Find category by code to get wastage/making/certification charges
      if (categoryCode && categories.length > 0) {
        const cat = categories.find(c => c.code === categoryCode);
        if (cat) {
          // Inherit from parent if child sale rates are 0 / not set
          const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
          const effectiveWastage = parseFloat(cat.sale_wastage_pct) > 0
            ? cat.sale_wastage_pct
            : (parent ? parent.sale_wastage_pct : '');
          const effectiveMaking = parseFloat(cat.sale_making_per_g) > 0
            ? cat.sale_making_per_g
            : (parent ? parent.sale_making_per_g : '');
          setCategoryInfo(cat);
          setWastagePercent(effectiveWastage || '');
          setMakingCharge(effectiveMaking || '');
        } else {
          // If direct match not found, try finding by name
          const catByName = categories.find(c => c.name?.toUpperCase().includes(categoryCode));
          if (catByName) {
            const parent = catByName.parent_id ? categories.find(c => c.id === catByName.parent_id) : null;
            const effectiveWastage = parseFloat(catByName.sale_wastage_pct) > 0
              ? catByName.sale_wastage_pct
              : (parent ? parent.sale_wastage_pct : '');
            const effectiveMaking = parseFloat(catByName.sale_making_per_g) > 0
              ? catByName.sale_making_per_g
              : (parent ? parent.sale_making_per_g : '');
            setCategoryInfo(catByName);
            setWastagePercent(effectiveWastage || '');
            setMakingCharge(effectiveMaking || '');
          } else {
            setCategoryInfo(null);
          }
        }
      }

      // Estimate overrides — applied last so they always win over system defaults
      if (estimateOverrides && itemFound) {
        if (estimateOverrides.stones) {
          // Estimates only snapshot sale price, not purchase cost — carry the real
          // cost_price forward from the item's current inventory stones (matched by
          // code) so the internal Cost Panel can still compute stone purchase cost.
          const inventoryStones = data.stones || [];
          const withCostPrice = estimateOverrides.stones.map(s => {
            const match = inventoryStones.find(inv => inv.stone_code === s.stone_code);
            return { ...s, cost_price: match ? match.cost_price : 0 };
          });
          setFetchedStones(withCostPrice);
        }
        if (estimateOverrides.wastage) setWastagePercent(estimateOverrides.wastage);
        if (estimateOverrides.making) setMakingCharge(estimateOverrides.making);
      }
    } catch (err) {
      console.error('Failed to fetch jewelry details', err);
      setItemCodeStatus('not_found');
      setItemCodeStatusMsg('Item Not Found');
      setFetchedStones([]);
      setDiamondCarats(0);
      setCertificationRequired(false);
      itemCodeRef.current?.focus();
    }
  };

  // Calculate diamond carats based on materials table and jewelry_stones
  const calculateDiamondCarats = async (jewelryId, stones) => {
    try {
      // Fetch from both Diamond and Flat Diamond categories
      const [diamondRes, flatDiamondRes] = await Promise.all([
        fetch('/api/materials/category/Diamond', { credentials: 'include' }),
        fetch('/api/materials/category/Flat Diamond', { credentials: 'include' })
      ])
      const [diamondData, flatDiamondData] = await Promise.all([
        diamondRes.json(),
        flatDiamondRes.json()
      ])

      const allDiamondMaterials = [
        ...(Array.isArray(diamondData) ? diamondData : []),
        ...(Array.isArray(flatDiamondData) ? flatDiamondData : [])
      ]

      if (allDiamondMaterials.length > 0) {
        const diamondCodes = allDiamondMaterials.map(material => material.code)
        console.log('Diamond codes from materials:', diamondCodes)

        // Filter stones that match diamond codes and calculate total weight
        const diamondStones = stones.filter(stone =>
          diamondCodes.includes(stone.stone_code)
        )

        const totalDiamondCarats = diamondStones.reduce((total, stone) =>
          total + (parseFloat(stone.weight) || 0), 0
        )

        console.log('Diamond stones found:', diamondStones)
        console.log('Total diamond carats:', totalDiamondCarats)

        setDiamondCarats(totalDiamondCarats)
      } else {
        setDiamondCarats(0)
      }
    } catch (err) {
      console.error('Failed to calculate diamond carats:', err)
      setDiamondCarats(0)
    }
  }

  const saveBilling = async (paymentsOverride = null) => {
    // Validate required fields
    if (!name || name.trim() === '') {
      alert('Please enter customer name');
      return null;
    }

    if (!grandTotalUSD || parseFloat(grandTotalUSD) <= 0) {
      alert('Please calculate the grand total before continuing');
      return null;
    }

    setIsSaving(true);

    try {
      // Prepare billing data
      const billingData = {
        customer_name: name,
        customer_phone: phone || null,
        customer_email: email || null,
        customer_address: null,
        customer_city: city || null,
        customer_state: state || null,
        item_code: itemCode || null,
        item_name: null,
        jewelry_category: categoryInfo?.name || null,
        gold_purity: purity || null,
        gross_weight: parseFloat(grossWeight) || null,
        stone_weight: parseFloat(stoneWeightTotal) || null,
        net_weight: parseFloat(netWeight) || null,
        gold_rate_24k: parseFloat(goldRate) || null,
        gold_value: parseFloat(goldValue) || null,
        stone_total: parseFloat(stoneTotal) || 0,
        making_charges: parseFloat(makingAmount) || 0,
        other_charges: parseFloat(wastageAmount) || 0,
        discount_amount: parseFloat(pdfTotals.discountAmount) || 0,
        grand_total: parseFloat(grandTotalUSD),
        currency: 'USD',
        customs_percent: parseFloat(pdfTotals.totalTaxPercent) || 0,
        customs_amount: parseFloat(pdfTotals.taxTotalINR) || 0,
        base_total: Math.max(0, (parseFloat(pdfTotals.grandTotalINR) || 0) - (parseFloat(pdfTotals.taxTotalINR) || 0)),
        stones: fetchedStones.length > 0 ? fetchedStones : null,
        notes: null,
        ...(estimateNumber ? { estimate_number: estimateNumber } : {}),
      };

      const response = await fetch('/api/billings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(billingData)
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Billing saved: ${result.data.bill_number}${itemCode ? ' | Item marked as SOLD' : ''}`);

        // Post payment rows — awaited so a rejected payment surfaces instead of
        // silently leaving the bill short-paid (shows "Completed" but saves as "partial").
        const billingId = result.data.id;
        const validPayments = (paymentsOverride || payments).filter(p => parseFloat(p.amount) > 0);
        const failedPayments = [];
        for (const p of validPayments) {
          try {
            const payRes = await fetch(`/api/billings/${billingId}/payments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                payment_type: p.payment_type,
                amount: parseFloat(p.amount),
                reference_number: p.reference_number || null,
                payment_date: p.payment_date || null,
                notes: p.notes || null,
                old_gold_items: p.old_gold_items || [],
              }),
            });
            const payData = await payRes.json().catch(() => ({}));
            if (!payRes.ok || !payData.success) {
              failedPayments.push(`${p.payment_type} ${p.amount}: ${payData.error || payRes.status}`);
            }
          } catch (err) {
            failedPayments.push(`${p.payment_type} ${p.amount}: ${err.message}`);
          }
        }
        if (failedPayments.length > 0) {
          alert(
            `Bill ${result.data.bill_number} was saved, but these payments could not be recorded:\n\n` +
            failedPayments.join('\n') +
            `\n\nThe bill will show as partially paid. Please add the missing payments from the bill.`
          );
        }

        // Record scheme redemptions for any gold_scheme payments
        const schemePayments = validPayments.filter(p => p.payment_type === 'gold_scheme' && p.enrollment_id);
        for (const p of schemePayments) {
          fetch('/api/schemes/redemptions/partial', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              enrollment_id: p.enrollment_id,
              amount: parseFloat(p.amount),
              purpose: `Billing #${result.data.bill_number}`,
            }),
          }).catch(err => console.warn('Scheme redemption post failed:', err));
        }

        // Mark source estimate as converted
        if (estimateId) {
          fetch(`/api/estimates/${estimateId}/convert`, { method: 'POST', credentials: 'include' })
            .catch(err => console.warn('Could not mark estimate as converted:', err));
          setEstimateId(null);
          setEstimateNumber('');
          setEstimateBannerVisible(false);
        }

        return result.data; // Return the saved billing data
      } else {
        alert(`Failed to save billing: ${result.error}\n${result.details ? result.details.join(', ') : ''}`);
        return null;
      }
    } catch (error) {
      console.error('Error saving billing:', error);
      alert('Failed to save billing. Please check your connection.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle view billing
  const handleViewBilling = () => {
    if (!itemCode) {
      alert('Please fill in the item code to view billing');
      return;
    }
    setShowDetailModal(true);
  };

  const handleProceedToPayment = () => {
    if (!name || name.trim() === '') { alert('Please enter customer name'); return; }
    if (!grandTotalUSD || parseFloat(grandTotalUSD) <= 0) { alert('Please calculate the grand total before continuing'); return; }
    setShowPaymentModal(true);
  };

  const handleDownloadInvoice = async () => {
    if (!name || name.trim() === '') { alert('Please enter customer name'); return; }
    const stoneCategoryTotals = buildStoneCategoryTotals(fetchedStones);
    const colorStoneCarats = fetchedStones
      .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - diamondCarats;
    let previewBillNumber = '';
    try {
      const res = await fetch('/api/billings/next-number/USD', { credentials: 'include' });
      const result = await res.json();
      if (result.success) previewBillNumber = result.data.billNumber;
    } catch (e) { /* preview number is optional */ }
    await PdfService.downloadInvoicePDF({
      currency: 'USD',
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      companyTagline: 'Hallmarked Certified Jewellery',
      date,
      billNumber: previewBillNumber,
      estimateNumber: previewBillNumber,
      customerName: name,
      customerPhone: phone,
      customerEmail: email,
      customerCity: city,
      customerState: state,
      itemCode,
      purity,
      goldPrice,
      grossWeight,
      netWeight,
      makingCharge,
      makingAmount,
      totalGoldAmount,
      stoneCategoryTotals,
      certificateDocuments,
      diamondCarats,
      colorStoneCarats: Math.max(0, colorStoneCarats),
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      customsDutyPercent: pdfTotals.totalTaxPercent,
      taxAmount: pdfTotals.taxTotalINR,
      grandTotalINR: pdfTotals.grandTotalINR,
      grandTotalUSD,
      grandTotal: pdfTotals.grandTotalINR,
      dollarRate,
      notes: '',
    }, 'USD');
  };

  const sendEmail = async (savedBilling) => {
    const stoneCategoryTotals = buildStoneCategoryTotals(fetchedStones);
    const colorStoneCarats = fetchedStones
      .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - diamondCarats;
    await EmailSendService.sendEmail({
      currency: 'USD',
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      companyTagline: 'Hallmarked Certified Jewellery',
      date,
      billNumber: savedBilling.bill_number,
      estimateNumber: savedBilling.bill_number,
      customerName: name,
      customerPhone: phone,
      customerEmail: email,
      customerCity: city,
      customerState: state,
      itemCode,
      jewelryName: jewelryName || itemCode,
      purity,
      grossWeight,
      netWeight,
      fineWeight,
      goldRate,
      goldPrice,
      goldValue,
      wastagePercent,
      wastageAmount,
      makingCharge,
      makingAmount,
      totalGoldAmount,
      stoneCategoryTotals,
      certificateDocuments,
      diamondCarats,
      colorStoneCarats: Math.max(0, colorStoneCarats),
      stoneTotal,
      certificationCharges: pdfTotals.certCharges,
      customsDutyPercent: pdfTotals.totalTaxPercent,
      taxLabel: `Customs (${pdfTotals.totalTaxPercent || 0}%)`,
      taxAmount: pdfTotals.taxTotalINR,
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      dollarRate,
      grandTotal: pdfTotals.grandTotalINR,
      grandTotalINR: pdfTotals.grandTotalINR,
      grandTotalUSD,
      notes: '',
      image_url: jewelryImage,
    }, email);
  };

  const sendWhatsApp = async (savedBilling) => {
    await WhatsAppLinkService.shareInvoice({
      customerName: name || 'Customer',
      phoneNumber: phone,
      date,
      itemCode,
      purity,
      grossWeight,
      netWeight,
      goldRate,
      wastagePercent,
      wastageAmount,
      makingCharge,
      makingAmount,
      goldAmount: totalGoldAmount,
      stoneAmount: stoneTotal,
      discount: (parseFloat(pdfTotals.discountAmount) || 0).toFixed(2),
      grandTotal: grandTotalUSD,
      billNumber: savedBilling.bill_number,
      currency: 'USD',
      companyName: 'GDS Jewellers',
      companyAddress: '15520 Pawnee St, Overland Park, KS - 66224',
    }, phone);
  };

  const clearForm = () => {
    setItemCode('');
    setJewelryName('');
    setPurity('');
    setGrossWeight('');
    setNetWeight('');
    setFineWeight('');
    setFinePercent(null);
    setKundanWeight('');
    setKundanPercent('');
    setGoldPrice('');
    setGoldValue('');
    setWastagePercent('');
    setWastageAmount('');
    setMakingCharge('');
    setMakingAmount('');
    setTotalGoldAmount('');
    setFetchedStones([]);
    setStoneTotal(0);
    setStoneWeightTotal(0);
    setStonePurchaseCost(0);
    setGrandTotalUSD('0.00');
    setPayments([]);
    setCustomerId(null);
    setItemCodeStatus(null);
    setItemCodeStatusMsg('');
    setDiamondCarats(0);
    setCertificationRequired(false);
    setCategoryInfo(null);
    setJewelryImage(null);
    setEstimateDiscount(0);
    setEstimateCustomsPct(null);
    setName('');
    setPhone('');
    setEmail('');
    setCity('');
    setState('');

    // Reset gold/dollar rate back to the live default (was possibly overridden by a loaded estimate)
    setGoldRate(todaysGoldRate || '');
    setGoldRateEditable(false);
    setDollarRate(todaysDollarRate || '');

    // Clear any estimate this bill was linked to via Load Estimate / Convert to Sale
    setEstimateNumber('');
    setEstimateId(null);
    setEstimateBannerVisible(false);

    // Force TotalSection to remount so its own local state (discount, Customs %)
    // resets too — those have no other reset path from here.
    setFormResetKey(k => k + 1);

    if (itemCodeRef.current) {
      itemCodeRef.current.focus();
    }
  };

  const handleLoadEstimate = (est) => {
    // Customer
    setName(est.customer_name || '');
    setPhone(est.customer_phone || '');
    setEmail(est.customer_email || '');

    // Rule 1: Gold rate — use whichever is higher (today vs quoted)
    const sysGold = parseFloat(goldRate) || 0;
    const estGold = parseFloat(est.gold_rate) || 0;
    const appliedGold = Math.max(sysGold, estGold);
    if (appliedGold > 0) { setGoldRate(appliedGold); setGoldRateEditable(true); }

    // Rule 2: Dollar rate — use whichever is lower (today vs quoted)
    const sysDollar = parseFloat(dollarRate) || 0;
    const estDollar = parseFloat(est.usd_to_inr_rate) || 0;
    if (sysDollar > 0 && estDollar > 0) {
      setDollarRate(Math.min(sysDollar, estDollar));
    } else if (estDollar > 0 && sysDollar === 0) {
      setDollarRate(estDollar);
    }

    // Item code — fetch jewelry details (purity, weight, image, categoryInfo)
    // then re-apply estimate values for stones/VA/making (rules 3 & 4)
    // Remap estimate stone format { code, name, rate } → billing format { stone_code, stone_name, sale_price }
    const estStones = Array.isArray(est.stone_details) && est.stone_details.length > 0
      ? est.stone_details.map(s => ({
        stone_code: s.stone_code || s.code || '',
        stone_name: s.stone_name || s.name || '',
        weight: s.weight,
        unit: s.unit || 'gram',
        sale_price: s.sale_price ?? s.rate ?? 0,
      }))
      : null;
    const estWastage = est.wastage_percent ? String(est.wastage_percent) : null;
    const estMaking = est.making_charge_per_gram ? String(est.making_charge_per_gram) : null;

    if (est.item_code) {
      setItemCode(est.item_code);
      // Pass estimate values as overrides — fetchJewelryDetails applies them after all system defaults
      setTimeout(() => fetchJewelryDetails(est.item_code, {
        stones: estStones,
        wastage: estWastage,
        making: estMaking,
      }), 300);
    } else {
      // No item code — apply estimate values directly
      if (estStones) setFetchedStones(estStones);
      if (estWastage) setWastagePercent(estWastage);
      if (estMaking) setMakingCharge(estMaking);
    }

    if (est.tax_rate) setEstimateCustomsPct(parseFloat(est.tax_rate));

    // Discount — stored in USD, passed via onTotalsChange to TotalSection via estimateDiscount
    if (parseFloat(est.discount_amount) > 0) setEstimateDiscount(parseFloat(est.discount_amount));

    setEstimateNumber(est.estimate_number || '');
    setEstimateId(est.id || null);
    setEstimateBannerVisible(true);
    setShowLoadModal(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageIdentifier pageId={SCREEN_IDS?.BILLING?.US_BILLING || 'BILL-002'} pageName="US Billing" />

      {/* ── Estimate pre-fill banner ── */}
      {estimateBannerVisible && estimateNumber && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between print:hidden">
          <span className="text-xs text-amber-800 font-medium">
            📋 Pre-filled from estimate <strong>{estimateNumber}</strong>. Confirm details and save to complete the sale.
          </span>
          <button
            onClick={() => { setEstimateBannerVisible(false); }}
            className="text-amber-500 hover:text-amber-700 text-xs underline ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Sticky Action Bar ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="px-4 py-2.5 flex items-center justify-between gap-3 max-w-5xl mx-auto">

          {/* Left: Title + secondary actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <h1 className="text-base font-bold text-slate-800 whitespace-nowrap flex items-center gap-2">
              <DollarSign size={16} className="text-green-500" />
              USD Billing
            </h1>
            <div className="w-px h-5 bg-slate-200" />
            <button onClick={() => setShowLoadModal(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 transition whitespace-nowrap">
              <Search size={12} /> Load Estimate
            </button>
            <button onClick={clearForm}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition">
              Clear
            </button>
          </div>

          {/* Centre: Primary actions */}
          <div className="flex items-center gap-1.5">
            <button onClick={handleViewBilling} disabled={!itemCode || isSaving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 transition">
              <Eye size={13} />
              View
            </button>
            <button onClick={handleDownloadInvoice} disabled={isSaving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-40 transition whitespace-nowrap">
              <FileText size={13} />
              Invoice PDF
            </button>
            <button onClick={handleProceedToPayment} disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 transition whitespace-nowrap">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              Checkout
            </button>
          </div>

          {/* Right: Gold Rate pill + USD Rate pill + Date pill */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Gold Rate pill */}
            <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-amber-600 whitespace-nowrap">24K Gold</span>
              <div className="w-px h-4 bg-amber-200" />
              {goldRateEditable ? (
                <div className="flex items-center gap-0.5">
                  <span className="text-sm font-bold text-amber-800">₹</span>
                  <input type="number" onFocus={e => e.target.select()} value={goldRate} onChange={(e) => setGoldRate(e.target.value)}
                    className="w-20 text-sm font-bold focus:outline-none bg-transparent text-amber-800 border-b border-amber-400" />
                </div>
              ) : (
                <span className="text-sm font-bold text-amber-800 whitespace-nowrap">
                  ₹{goldRate ? parseFloat(goldRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </span>
              )}
            </div>
            {/* USD Rate pill */}
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-green-600 whitespace-nowrap">USD</span>
              <div className="w-px h-4 bg-green-200" />
              <span className="text-xs text-green-500">₹/</span>
              <input type="number" onFocus={e => e.target.select()} value={dollarRate} onChange={(e) => setDollarRate(e.target.value)}
                className="w-14 text-sm font-bold focus:outline-none bg-transparent text-green-800 border-b border-green-300" />
            </div>
            {/* Date pill */}
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input type="date" value={date} onChange={handleDateChange}
                className="text-xs font-medium text-slate-700 focus:outline-none bg-transparent cursor-pointer" />
            </div>
          </div>
        </div>
        {(goldRateError || goldRateWarn) && (
          <div className="px-4 pb-1.5 flex gap-3">
            {goldRateError && <p className="text-xs text-red-500">{goldRateError}</p>}
            {goldRateWarn && <p className="text-xs text-amber-500">{goldRateWarn}</p>}
          </div>
        )}
      </div>

      <div className="p-4 pb-20 max-w-6xl mx-auto">

        {/* ── Unified two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

          {/* ══ LEFT COLUMN ══ */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Customer Details */}
            <div className="relative z-10 bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <User className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Customer</span>
              </div>
              <div className="p-3">
                <CustomerLookup
                  customerName={name} customerPhone={phone}
                  customerEmail={email} customerCity={city} customerState={state}
                  country="United States"
                  onChange={(field, value) => {
                    if (field === 'customerName') setName(value)
                    else if (field === 'customerPhone') setPhone(value)
                    else if (field === 'customerEmail') setEmail(value)
                    else if (field === 'customerCity') setCity(value)
                    else if (field === 'customerState') setState(value)
                  }}
                  onCustomerLoaded={(customer) => setCustomerId(customer?.id || null)}
                />
              </div>
            </div>

            {/* Jewelry & Gold Calculation */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <Gem className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Item & Gold</span>
              </div>
              <div className="p-3 space-y-2">
                {/* Item Code + Description */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Item Code</label>
                    <div className="flex flex-col gap-0.5">
                      <input
                        type="text"
                        value={itemCode}
                        ref={itemCodeRef}
                        onChange={e => { setItemCode(e.target.value.toUpperCase()); setItemCodeStatus(null); setItemCodeStatusMsg(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchJewelryDetails(itemCode); e.target.blur(); } }}
                        className={`w-full border-2 rounded-lg px-3 py-1.5 text-sm font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${itemCodeStatus === 'sold' ? 'border-red-300 bg-red-50' : itemCodeStatus === 'not_found' ? 'border-yellow-300 bg-yellow-50' : 'border-amber-300 bg-amber-50'}`}
                        placeholder="e.g. DNS-1" />
                      {itemCodeStatus === 'sold' && <span className="text-xs font-semibold text-red-600">🔴 {itemCodeStatusMsg}</span>}
                      {itemCodeStatus === 'not_found' && <span className="text-xs font-semibold text-yellow-600">⚠️ {itemCodeStatusMsg}</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Description</label>
                    <input type="text" value={jewelryName} onChange={e => setJewelryName(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                      placeholder="Name" />
                  </div>
                </div>

                {/* Weight row */}
                <div className="grid grid-cols-3 gap-2">
                  {[['Gross Wt', grossWeight], ['Net Wt', netWeight], ['Fine Wt', fineWeight]].map(([lbl, val]) => (
                    <div key={lbl}>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{lbl}</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {val || <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Purity / Kundan Wt / Gold Value */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Purity</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {purity ? `${purity}K` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  {isKundanCategory(categoryInfo) ? (
                    <div>
                      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Kundan Wt</label>
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {kundanWeight ? `${parseFloat(kundanWeight).toFixed(3)}g` : <span className="text-slate-300">—</span>}
                      </div>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Gold Value</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {goldValue ? `₹${parseFloat(goldValue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                </div>

                {/* VA % / Making/g — editable for manager/admin/super_admin */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA %</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} step="0.001" value={wastagePercent} onChange={e => setWastagePercent(e.target.value)} onKeyDown={blurOnEnter}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="VA %" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {wastagePercent || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making/g</label>
                    {canEditRate ? (
                      <input type="number" onFocus={e => e.target.select()} value={makingCharge} onChange={e => setMakingCharge(e.target.value)} onKeyDown={blurOnEnter}
                        className="w-full border border-amber-400 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="Making/g" />
                    ) : (
                      <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                        {makingCharge || <span className="text-slate-300">—</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* VA Amt / Making Amt / Total Gold */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">VA Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {wastageAmount ? `₹${parseFloat(wastageAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Making Amt</label>
                    <div className="w-full border border-slate-100 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-700 font-medium">
                      {makingAmount ? `₹${parseFloat(makingAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Total Gold</label>
                    <div className="w-full border border-amber-200 rounded-lg px-3 py-1.5 text-sm bg-amber-50 text-amber-800 font-bold">
                      {totalGoldAmount ? `₹${parseFloat(totalGoldAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-amber-300">—</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stone Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <Gem className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Stone Details</span>
              </div>
              <StoneSection
                onStoneTotalChange={({ saleTotal, purchaseTotal, totalWeight }) => {
                  setStoneTotal(saleTotal);
                  setStonePurchaseCost(purchaseTotal);
                  setStoneWeightTotal(totalWeight || 0);
                }}
                initialStones={fetchedStones}
              />
            </div>
          </div>{/* end left col */}

          {/* ══ RIGHT COLUMN — sticky ══ */}
          <div className="lg:col-span-1 flex flex-col gap-4 sticky top-[60px] self-start">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <DollarSign className="text-slate-300" size={14} />
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Totals & Taxes</span>
              </div>
              <div className="p-3">
                <TotalSection
                  key={formResetKey}
                  totalGoldAmount={totalGoldAmount}
                  goldValue={goldValue}
                  wastageAmount={wastageAmount}
                  makingAmount={makingAmount}
                  stoneTotal={stoneTotal}
                  stonePurchaseCost={stonePurchaseCost}
                  dollarRate={dollarRate}
                  diamondCarats={diamondCarats}
                  certificationRequired={certificationRequired}
                  setCertificationRequired={setCertificationRequired}
                  setDiamondCarats={setDiamondCarats}
                  categoryInfo={categoryInfo}
                  setParentGrandTotalUSD={setGrandTotalUSD}
                  itemCode={itemCode}
                  grossWeight={grossWeight}
                  netWeight={netWeight}
                  fineWeight={fineWeight}
                  goldRate={goldRate}
                  purity={purity}
                  finePercent={finePercent}
                  kundanWeight={kundanWeight}
                  kundanPercent={kundanPercent}
                  fetchedStones={fetchedStones}
                  onTotalsChange={setPdfTotals}
                  initialCustomsPct={estimateCustomsPct}
                  initialDiscount={estimateDiscount}
                  entryRates={entryRates}
                  todaysGoldRate={todaysGoldRate}
                  todaysDollarRate={todaysDollarRate}
                  billDate={date}
                />
              </div>
            </div>

            {/* Item Image */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-600 px-4 py-2 flex items-center gap-2 rounded-t-xl">
                <svg className="text-slate-300 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-white font-semibold text-xs tracking-widest uppercase">Item Image</span>
              </div>
              {jewelryImage ? (
                <img
                  src={jewelryImage}
                  alt={itemCode || 'Jewelry'}
                  className="w-full object-contain"
                />
              ) : (
                <div className="w-full aspect-square bg-slate-50 flex flex-col items-center justify-center gap-3">
                  <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-slate-300 text-xs tracking-wide">Enter item code to load image</span>
                </div>
              )}
            </div>
          </div>

        </div>

      </div>

      {/* Detail Modal for viewing billing */}
      <EstimateDetailModal
        isOpen={showDetailModal}
        estimate={{
          is_billing_draft: true,
          estimate_number: 'DRAFT',
          customer_name: name || 'Customer',
          customer_phone: phone,
          customer_email: email,
          customer_city: city,
          customer_state: state,
          date: date,
          item_code: itemCode,
          gold_purity: purity,
          gross_weight: grossWeight,
          net_weight: netWeight,
          fine_weight: fineWeight,
          gold_rate: goldRate,
          gold_price: goldPrice,
          gold_value: goldValue,
          wastage_percent: wastagePercent,
          wastage_amount: wastageAmount,
          making_charge_per_gram: makingCharge,
          making_amount: makingAmount,
          total_gold_amount: totalGoldAmount,
          stone_total: stoneTotal,
          diamond_carats: diamondCarats,
          color_stone_carats: Math.max(0, fetchedStones
            .filter(s => (s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct')
            .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0) - diamondCarats),
          certification_required: certificationRequired,
          currency: 'USD',
          usd_to_inr_rate: dollarRate,
          grand_total_usd: grandTotalUSD,
          notes: ''
        }}
        onClose={() => setShowDetailModal(false)}
      />

      <PaymentConfirmModal
        isOpen={showPaymentModal}
        onClose={(wasSaved) => { setShowPaymentModal(false); if (wasSaved) clearForm(); }}
        grandTotal={parseFloat(grandTotalUSD) || 0}
        currency="USD"
        customerId={customerId}
        dollarRate={parseFloat(dollarRate) || null}
        goldRate={parseFloat(goldRate) || null}
        customerName={name}
        customerPhone={phone}
        customerEmail={email}
        onSaveAndConfirm={(pmts) => saveBilling(pmts)}
        onSendEmail={sendEmail}
        onSendWhatsApp={sendWhatsApp}
      />
      {showLoadModal && (
        <LoadEstimateModal
          currency="USD"
          onClose={() => setShowLoadModal(false)}
          onLoad={handleLoadEstimate}
        />
      )}
    </div>
  );
};

// Stone Section Component
function StoneSection({ onStoneTotalChange, initialStones = [] }) {
  const [stones, setStones] = useState([]);

  // Accept initial stones from parent
  useEffect(() => {
    if (Array.isArray(initialStones) && initialStones.length > 0) {
      setStones(initialStones.map((stone, idx) => {
        const weight = parseFloat(stone.weight) || 0;
        const unit = stone.unit || 'CARAT';
        return {
          id: stone.id || idx,
          code: stone.stone_code || '',
          name: stone.stone_name || '',
          weight,
          unit,
          purchaseRate: parseFloat(stone.cost_price) || 0,
          rate: parseFloat(stone.sale_price) || 0,
          cost: weight * (parseFloat(stone.sale_price) || 0),
          purchaseCost: weight * (parseFloat(stone.cost_price) || 0),
        };
      }));
    } else {
      // Clear stones when initialStones is empty
      setStones([]);
    }
  }, [initialStones]);

  useEffect(() => {
    const total = stones.reduce((sum, stone) => sum + parseFloat(stone.cost || 0), 0);
    const purchaseTotal = stones.reduce((sum, stone) => sum + parseFloat(stone.purchaseCost || 0), 0);
    // Unit-aware: convert each stone's weight to grams before summing
    const totalWeight = stones.reduce((sum, stone) => sum + stoneWeightToGrams(stone.weight, stone.unit), 0);
    onStoneTotalChange({ saleTotal: total, purchaseTotal, totalWeight });
  }, [stones, onStoneTotalChange]);

  const updateStone = (id, field, value) => {
    setStones(stones.map(stone => {
      if (stone.id === id) {
        const updatedStone = { ...stone, [field]: parseFloat(value) || 0 };
        if (field === 'rate') {
          updatedStone.cost = updatedStone.weight * updatedStone.rate;
        }
        return updatedStone;
      }
      return stone;
    }));
  };

  return (
    <div className="space-y-2">
      {stones.length > 0 && (
        <div className="mt-1">
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border px-2 py-1 text-left">Stone Code</th>
                  <th className="border px-2 py-1 text-left">Stone Name</th>
                  <th className="border px-2 py-1 text-left">Weight</th>
                  <th className="border px-2 py-1 text-left">Unit</th>
                  <th className="border px-2 py-1 text-left">Rate</th>
                  <th className="border px-2 py-1 text-left">Cost (₹)</th>
                </tr>
              </thead>
              <tbody>
                {[...stones].sort((a, b) => {
                  const u = s => (s.unit || '').toUpperCase();
                  const unitOrder = u => (u === 'CARAT' || u === 'CARATS' || u === 'CT') ? 0 : (u === 'GRAM' || u === 'GRAMS' || u === 'G') ? 1 : 2;
                  const uDiff = unitOrder(u(a)) - unitOrder(u(b));
                  if (uDiff !== 0) return uDiff;
                  return (a.code || '').localeCompare(b.code || '');
                }).map(stone => (
                  <tr key={stone.id}>
                    <td className="border px-2 py-1">{stone.code}</td>
                    <td className="border px-2 py-1">{stone.name}</td>
                    <td className="border px-2 py-1">{(() => { const u = (stone.unit || '').toUpperCase(); return u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER' ? stone.weight : parseFloat(stone.weight).toFixed(2); })()}</td>
                    <td className="border px-2 py-1">{(() => { const u = (stone.unit || '').toUpperCase(); return u === 'CARAT' || u === 'CARATS' || u === 'CT' ? 'Ct' : u === 'GRAM' || u === 'GRAMS' || u === 'G' ? 'g' : u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER' ? 'pcs' : stone.unit || 'Ct'; })()}</td>
                    <td className="border px-2 py-1">
                      <input
                        type="number" onFocus={e => e.target.select()}
                        value={stone.rate}
                        onChange={e => updateStone(stone.id, 'rate', e.target.value)}
                        className="border rounded px-1 py-0.5 text-xs w-16"
                      />
                    </td>
                    <td className="border px-2 py-1">₹{parseFloat(stone.cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-1 text-right">
            <span className="font-bold">Total Stone Cost: ₹{stones.reduce((sum, stone) => sum + stone.cost, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}
      {stones.length === 0 && (
        <div className="text-center text-gray-500 py-4">
          No stones data available
        </div>
      )}
    </div>
  );
}

// Total Section Component - Modified for US Billing
function TotalSection({ totalGoldAmount, goldValue, wastageAmount, makingAmount, stoneTotal, stonePurchaseCost, dollarRate, diamondCarats: propDiamondCarats, certificationRequired: propCertificationRequired, setCertificationRequired, setDiamondCarats, categoryInfo, setParentGrandTotalUSD, itemCode = '', grossWeight, netWeight, fineWeight, goldRate, purity, finePercent, kundanWeight, kundanPercent, fetchedStones, onTotalsChange, initialCustomsPct, initialDiscount = 0, entryRates = { goldRate: null, dollarRate: null, shipmentCostUSD: 0, laborCharges: 0, otherCosts: 0, inventoryNetWeight: '' }, todaysGoldRate = null, todaysDollarRate = null, billDate = '' }) {
  const { hasPermission } = usePermissions();
  const isUser = !hasPermission('us-billing', 'edit');
  const [certChargePerCarat, setCertChargePerCarat] = useState(0); // Will be set from category
  const [totalTaxPercent, setTotalTaxPercent] = useState(initialCustomsPct ?? 18); // Default 18% customs duty
  const [taxTotal, setTaxTotal] = useState('0.00');
  const [grandTotal, setGrandTotal] = useState('0.00');
  // eslint-disable-next-line no-unused-vars
  const [grandTotalUSD, setGrandTotalUSD] = useState('0.00');
  const [certificationRequired, setCertificationRequiredLocal] = useState(propCertificationRequired || false);
  const [diamondCarats, setDiamondCaratsLocal] = useState(propDiamondCarats || 0);
  const [calculatedCertCharges, setCalculatedCertCharges] = useState(0);
  const [discount, setDiscount] = useState(initialDiscount || 0);

  // Sync discount when an estimate is loaded after mount
  useEffect(() => {
    if (initialDiscount > 0) setDiscount(initialDiscount);
  }, [initialDiscount]);

  // Hold-to-peek cost panel (hold backtick ` key) — admin/manager only
  const [showCostPanel, setShowCostPanel] = useState(false);
  useEffect(() => {
    if (isUser) return; // disabled for role=user
    const onDown = (e) => { if (e.key === '`') { e.preventDefault(); if (!e.repeat) setShowCostPanel(true); } };
    const onUp = (e) => { if (e.key === '`') setShowCostPanel(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [isUser]);

  // Update certification charge per carat when category changes
  useEffect(() => {
    if (categoryInfo && categoryInfo.certification_charges) {
      setCertChargePerCarat(categoryInfo.certification_charges);
    } else {
      setCertChargePerCarat(0);
    }
  }, [categoryInfo]);

  // Sync customs % when pre-filled from estimate
  useEffect(() => {
    if (initialCustomsPct != null) setTotalTaxPercent(initialCustomsPct);
  }, [initialCustomsPct]);

  // Sync with parent props
  useEffect(() => {
    setCertificationRequiredLocal(propCertificationRequired || false);
  }, [propCertificationRequired]);

  useEffect(() => {
    setDiamondCaratsLocal(propDiamondCarats || 0);
  }, [propDiamondCarats]);

  // Update parent when local state changes
  // const handleCertificationChange = (value) => { // Not used in current implementation
  //   setCertificationRequiredLocal(value);
  //   if (setCertificationRequired) {
  //     setCertificationRequired(value);
  //   }
  // };

  // const handleDiamondCaratsChange = (value) => { // Not used in current implementation
  //   setDiamondCaratsLocal(value);
  //   if (setDiamondCarats) {
  //     setDiamondCarats(value);
  //   }
  // };

  // Customs duty is fixed at 18% — no DB fetch needed

  // Calculate certification charges and totals
  // Small items ("V" category): priced as cost + 30%, rounded up to $100.
  const smallItem = isSmallItemCode(itemCode);

  // Canonical ACTUAL COST in ₹ (gold + stones + labor + cert + ~18% customs),
  // matching the Cost Panel. Drives the small-item price. Cost logic unchanged.
  const actualCostINR = useMemo(() => {
    const isKundan = isKundanItemCode(itemCode);
    const nw = parseFloat(entryRates.inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
    const rate24k = parseFloat(goldRate) || 0;
    const kw = parseFloat(kundanWeight) || 0;
    const goldNetWt = parseFloat(entryRates.inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
    const kundanFineWt = isKundan && finePercent > 0
      ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
      : 0;
    const goldAmtRaw = isKundan && kundanFineWt > 0
      ? Math.ceil(kundanFineWt * rate24k)
      : Math.ceil(calculateGoldValue(calculatePurityGoldRate(rate24k, purity), nw));
    const goldAmt = applyKundanGoldCostDeduction(goldAmtRaw, itemCode); // Kundan: −6%
    const stoneCostAmt = Math.ceil(parseFloat(stonePurchaseCost || 0));
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    const laborCost = Math.ceil(nw * (entryRates.laborCharges || 0));
    const { totalCost } = calculateAcquisitionCostWithTax({ goldCost: goldAmt, stoneCost: stoneCostAmt, laborCost, certCost: certAmt, otherCost: entryRates.otherCosts, taxPercent: 18 });
    return totalCost;
  }, [itemCode, netWeight, goldRate, purity, kundanWeight, kundanPercent, finePercent, stonePurchaseCost, diamondCarats, certChargePerCarat, certificationRequired, entryRates]);

  const smallItemPriceUSD = useMemo(() => {
    if (!smallItem) return 0;
    const dr = parseFloat(dollarRate) || 0;
    if (dr <= 0 || actualCostINR <= 0) return 0;
    return calculateSmallItemPrice(actualCostINR / dr, 100);
  }, [smallItem, dollarRate, actualCostINR]);

  useEffect(() => {
    const certAmt = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificationRequired);
    setCalculatedCertCharges(certAmt);

    const jewelValueINR = calculateJewelValue(totalGoldAmount, stoneTotal, certAmt);
    const normal = calculateUSDGrandTotal(jewelValueINR, totalTaxPercent, dollarRate);

    const dr = parseFloat(dollarRate) || 1;
    const useSmall = smallItem && smallItemPriceUSD > 0;
    // V items: cost + 30%, or the normal price when staff-raised making / VA /
    // stone rates push it higher (leaves room to negotiate down).
    const vPriceUSD = Math.max(smallItemPriceUSD, normal.grandTotalUSD);
    const grandUSD = useSmall ? vPriceUSD : normal.grandTotalUSD;
    const grandTotalINR = useSmall ? Math.ceil(vPriceUSD * dr) : normal.grandTotalINR;
    const taxAmountINR = useSmall ? 0 : normal.taxAmountINR;

    setTaxTotal(taxAmountINR.toFixed(2));
    setGrandTotal(grandTotalINR.toFixed(2));
    setGrandTotalUSD(grandUSD.toFixed(2));

    const discountUSD = Math.min(parseFloat(discount) || 0, grandUSD);
    const finalUSD = Math.max(0, grandUSD - discountUSD);

    if (setParentGrandTotalUSD) {
      setParentGrandTotalUSD(finalUSD.toFixed(2));
    }

    if (onTotalsChange) {
      onTotalsChange({
        totalTaxPercent: parseFloat(totalTaxPercent) || 0,
        taxTotalINR: taxAmountINR.toFixed(2),
        grandTotalINR: grandTotalINR.toFixed(2),
        certCharges: certAmt,
        discountAmount: discountUSD,
        smallItem: useSmall,
      });
    }
  }, [totalGoldAmount, stoneTotal, certChargePerCarat, totalTaxPercent, dollarRate, certificationRequired, diamondCarats, discount, setParentGrandTotalUSD, onTotalsChange, smallItem, smallItemPriceUSD]);

  return (
    <div className="space-y-3">

      {/* ── Price Breakdown Card ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-slate-700 px-4 py-2.5 flex items-center gap-2 rounded-t-xl">
          <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
          </svg>
          <span className="text-white font-semibold text-xs tracking-widest uppercase">Price Breakdown</span>
        </div>

        {/* Line items — hidden for small ("V") items: only weights + final price shown */}
        {!smallItem && (
        <div className="divide-y divide-slate-100">
          {/* Gold Amount */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">Gold Amount</span>
            </div>
            <span className="text-sm font-semibold text-slate-800">
              ₹{Math.ceil(parseFloat(totalGoldAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Stone Cost — only if non-zero */}
          {parseFloat(stoneTotal || 0) > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Stone Cost</span>
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{Math.ceil(parseFloat(stoneTotal || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Certification — only if non-zero */}
          {calculatedCertCharges > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                <span className="text-sm text-slate-600">Certification</span>
                {certChargePerCarat > 0 && (
                  <span className="text-xs text-slate-400">(₹{parseFloat(certChargePerCarat).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/ct)</span>
                )}
              </div>
              <span className="text-sm font-semibold text-slate-800">
                ₹{calculatedCertCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Subtotal */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
            <span className="text-sm font-medium text-slate-700">Subtotal</span>
            <span className="text-sm font-bold text-slate-800">
              ₹{(Math.ceil(parseFloat(totalGoldAmount || 0)) + Math.ceil(parseFloat(stoneTotal || 0)) + calculatedCertCharges).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Customs — editable % inline (manager/admin/super_admin only) */}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0"></span>
              <span className="text-sm text-slate-600">Customs</span>
              {!isUser ? (
                <div className="flex items-center border border-amber-400 rounded-md overflow-hidden">
                  <input
                    type="number" onFocus={e => e.target.select()}
                    value={totalTaxPercent || ''}
                    onChange={e => setTotalTaxPercent(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    className="w-14 text-center text-xs py-0.5 px-1 focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                  />
                  <span className="bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600 border-l border-amber-400">%</span>
                </div>
              ) : (
                <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded px-2 py-0.5">{totalTaxPercent || 0}%</span>
              )}
            </div>
            <span className="text-sm font-semibold text-slate-700">
              ₹{parseInt(taxTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        )}

        {/* Grand Total INR footer — hidden for small items (USD price only) */}
        {!smallItem && (
        <div className="bg-slate-600 px-4 py-2.5 flex items-center justify-between">
          <p className="text-slate-200 text-xs uppercase tracking-widest font-medium">Grand Total (INR)</p>
          <span className="text-white text-lg font-bold tracking-tight">
            ₹{parseInt(grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        )}

        {/* Grand Total USD footer */}
        <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
          <p className="text-slate-300 text-xs uppercase tracking-widest font-medium">Grand Total (USD)</p>
          <span className="text-white text-2xl font-bold tracking-tight">
            ${parseFloat(grandTotalUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {/* Discount row */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-300">Round Off</span>
            <div className="flex items-center border border-green-500 rounded overflow-hidden">
              <span className="bg-slate-600 px-1.5 py-0.5 text-xs text-green-400 border-r border-green-500">$</span>
              <input
                type="number" onFocus={e => e.target.select()}
                value={discount || ''}
                onChange={e => setDiscount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                placeholder="0"
                min="0"
                step="0.01"
                className="w-20 text-center text-xs py-0.5 px-1 focus:outline-none bg-slate-700 text-white placeholder-slate-400"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const cur = parseFloat(grandTotalUSD) || 0;
                const disc = Math.round((cur % 1) * 100) / 100;
                setDiscount(disc);
              }}
              className="text-xs px-2 py-0.5 rounded border border-green-500 bg-slate-600 text-green-400 hover:bg-slate-500 transition whitespace-nowrap"
            >
              Round ↓$1
            </button>
          </div>
          {parseFloat(discount) > 0 && (
            <span className="text-sm font-semibold text-green-400">
              −${parseFloat(discount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>

        {/* Final Amount */}
        {parseFloat(discount) > 0 && (() => {
          const finalUSD = Math.max(0, (parseFloat(grandTotalUSD) || 0) - (parseFloat(discount) || 0));
          const finalINR = Math.round(finalUSD * (parseFloat(dollarRate) || 0));
          return (
            <div className="bg-emerald-700 px-4 py-3 flex items-center justify-between">
              <p className="text-emerald-100 text-xs uppercase tracking-widest font-medium">Final Amount</p>
              <div className="text-right">
                <p className="text-white text-2xl font-bold tracking-tight">${finalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-emerald-200 text-xs mt-0.5">≈ ₹{finalINR.toLocaleString('en-IN')}</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── USD / INR conversion pills ── */}
      <div className="flex gap-2">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Rate Used</p>
          <p className="text-sm font-bold text-slate-700 mt-0.5">₹{parseFloat(dollarRate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / $1</p>
        </div>
        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-green-600 font-medium uppercase tracking-wide">USD Total</p>
          <p className="text-sm font-bold text-green-700 mt-0.5">${parseFloat(grandTotalUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* ── HOLD-TO-PEEK COST PANEL (print:hidden, owner only) ── */}
      {showCostPanel && (() => {
        const isKundan = isKundanItemCode(itemCode);

        // ── Cost recalculation ──
        const _gw = parseFloat(grossWeight) || 0; // eslint-disable-line no-unused-vars
        const nw = parseFloat(entryRates.inventoryNetWeight) || parseFloat(netWeight) || 0; // Cost Panel: physical inventory Net Wt
        const kw = parseFloat(kundanWeight) || 0;
        const goldNetWt = parseFloat(entryRates.inventoryNetWeight) > 0 ? nw : Math.max(0, nw - kw);
        // For Kundan: use true fine weight (net + Kundan contribution); for others: use purity-adjusted gold value
        const fw = finePercent > 0
          ? calculateKundanFineWeight(goldNetWt, finePercent, kw, kundanPercent)
          : parseFloat(fineWeight) || 0;
        const currentGoldRate = parseFloat(goldRate) || 0;
        const currentDollarRate = parseFloat(dollarRate) || 1;

        const goldCostRaw = isKundan
          ? Math.ceil(fw * currentGoldRate)
          : Math.ceil(calculatePurityGoldRate(currentGoldRate, purity) * nw);
        const goldCost = applyKundanGoldCostDeduction(goldCostRaw, itemCode); // Kundan: −6%
        const stoneCost = Math.ceil(parseFloat(stonePurchaseCost) || 0);
        const certCost = calculatedCertCharges;
        const laborCost = Math.ceil(nw * (entryRates.laborCharges || 0));
        const preTaxCost = calculateAcquisitionCost({ goldCost, stoneCost, laborCost, certCost, otherCost: entryRates.otherCosts });
        // Use actual prorated shipment cost if recorded; fall back to 18% estimate
        const hasShipmentCost = entryRates.shipmentCostUSD > 0;
        const taxCost = hasShipmentCost
          ? Math.ceil(entryRates.shipmentCostUSD * currentDollarRate)
          : Math.ceil((preTaxCost * 18) / 100);
        const taxLabel = hasShipmentCost ? '🚚 Shipment Cost' : 'Tax (Customs ~18%)';

        const quotedCost = parseFloat(grandTotal || 0);
        const {
          actualCost, profit, profitPct,
          actualCostUSD = 0, quotedCostUSD = 0, profitUSD = 0,
        } = calculateCostPanelResult({ preTaxCost, taxAmount: taxCost, quotedCost, dollarRate });

        return (
          <CostPanel
            ratePill={
              <RatePill
                todayGoldRate={todaysGoldRate}
                usedGoldRate={currentGoldRate}
                todayDollarRate={todaysDollarRate}
                usedDollarRate={currentDollarRate}
                usedDate={billDate}
              />
            }
            rows={[
              { label: isKundan ? 'Gold Cost (Kundan −6%)' : 'Gold Cost', value: goldCost },
              { label: 'Stones Cost', value: stoneCost },
              { label: `Labor${entryRates.laborCharges ? ` (₹${entryRates.laborCharges}/g)` : ''}`, value: laborCost },
              { label: 'Certificate', value: certCost },
              { label: 'Other Costs', value: entryRates.otherCosts || 0 },
              { label: taxLabel, value: taxCost },
            ]}
            quotedCost={quotedCost}
            actualCost={actualCost}
            quotedCostUSD={quotedCostUSD}
            actualCostUSD={actualCostUSD}
            profit={profit}
            profitPct={profitPct}
            profitUSD={profitUSD}
          />
        );
      })()}
    </div>
  );
}

export default USBilling;
